import { useCallback, useEffect, useState } from "react";
import { HardHat, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Panel, SectionLabel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Row = ({ t, onChanged }) => {
  const [name, setName] = useState(t.name);
  const [rate, setRate] = useState(t.hourly_rate ?? "");
  const rateNum = rate === "" ? null : Number(rate);
  const dirty = name.trim() !== t.name || rateNum !== (t.hourly_rate ?? null);
  const save = async (active = t.active) => {
    try { await api.put(`/external-technicians/${t.id}`, { name, active, hourly_rate: rateNum }); toast.success(active === t.active ? "Tecnico aggiornato" : active ? "Tecnico riattivato" : "Tecnico disattivato"); onChanged(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <li className="flex flex-wrap items-center gap-2 py-2" data-testid={`ext-tech-${t.id}`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 max-w-xs text-sm" data-testid={`ext-tech-name-${t.id}`} />
      <div className="flex items-center gap-1 text-xs text-slate-500"><Input type="number" min="0" step="0.5" value={rate} placeholder="Tariffa" onChange={(e) => setRate(e.target.value)} className="h-8 w-24 text-sm" data-testid={`ext-tech-rate-${t.id}`} />€/ora</div>
      {dirty && <Button size="sm" variant="outline" className="h-8" onClick={() => save()} data-testid={`ext-tech-save-${t.id}`}>Salva</Button>}
      <StatusBadge tone={t.active ? "green" : "neutral"}>{t.active ? "Nell'elenco" : "Disattivato"}</StatusBadge>
      <span className="text-xs text-slate-400">{t.reports} resoconti</span>
      <Button size="sm" variant="ghost" className="ml-auto h-8" onClick={() => save(!t.active)} data-testid={`ext-tech-toggle-${t.id}`}>{t.active ? "Disattiva" : "Riattiva"}</Button>
    </li>
  );
};

export const ExternalTechniciansPanel = () => {
  const [items, setItems] = useState(null);
  const [name, setName] = useState("");
  const load = useCallback(() => api.get("/external-technicians").then((r) => setItems(r.data.items)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/external-technicians", { name }); setName(""); toast.success("Tecnico aggiunto all'elenco"); load(); }
    catch (x) { toast.error(errorMessage(x)); }
  };
  if (!items) return null;
  return (
    <Panel className="mb-4 p-4" data-testid="ext-tech-panel">
      <div className="mb-1 flex items-center gap-2"><HardHat className="h-4 w-4 text-slate-400" /><SectionLabel>Tecnici esterni</SectionLabel></div>
      <p className="text-xs text-slate-500">Nel modulo il tecnico sceglie il suo nome da questo elenco. Se l'elenco è vuoto, scrive nome e cognome a mano. Chi apre il modulo vede solo i nomi dei tecnici attivi.</p>
      <form onSubmit={add} className="mt-3 flex gap-2">
        <Input placeholder="Nome e cognome" value={name} onChange={(e) => setName(e.target.value)} className="h-9 max-w-xs" data-testid="ext-tech-new-name" />
        <Button size="sm" type="submit" disabled={name.trim().length < 2} className="gap-1" data-testid="ext-tech-add"><Plus className="h-4 w-4" />Aggiungi</Button>
      </form>
      {items.length > 0 && <ul className="mt-2 divide-y divide-line">{items.map((t) => <Row key={`${t.id}-${t.name}-${t.active}-${t.hourly_rate}`} t={t} onChanged={load} />)}</ul>}
    </Panel>
  );
};
